import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Package, CheckCircle, XCircle, Clock, Phone, Building2, DollarSign, Star, X, Search, Image as ImageIcon } from 'lucide-react';
import { isAdminUser } from '../utils/adminClaim';

function AdminProductApproval({ userInfo }) {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const loadApplications = useCallback(async () => {
    try {
      setLoading(true);
      let q;
      if (filter === 'all') {
        q = query(collection(db, 'newProducts'));
      } else {
        q = query(collection(db, 'newProducts'), where('status', '==', filter));
      }
      const snapshot = await getDocs(q);
      const apps = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      apps.sort((a, b) => {
        const aTime = b.createdAt?.toMillis?.() || 0;
        const bTime = a.createdAt?.toMillis?.() || 0;
        return aTime - bTime;
      });
      setApplications(apps);
    } catch (error) {
      console.error('신제품 신청서 로딩 실패:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (!isAdminUser(userInfo)) {
      alert('관리자만 접근 가능합니다.');
      window.location.href = '/';
      return;
    }
    loadApplications();
  }, [userInfo, filter, loadApplications]);

  const handleApprove = async (product) => {
    if (!window.confirm(`"${product.productName}" 신제품을 승인하시겠습니까?`)) return;
    try {
      await updateDoc(doc(db, 'newProducts', product.id), {
        status: 'approved',
        approvedBy: userInfo.uid,
        approvedAt: Timestamp.now()
      });
      alert('신제품 승인이 완료되었습니다!');
      setShowModal(false);
      loadApplications();
    } catch (error) {
      console.error('승인 처리 실패:', error);
      alert('승인 처리 중 오류가 발생했습니다.');
    }
  };

  const handleReject = async (product) => {
    const reason = window.prompt('거부 사유를 입력하세요:');
    if (!reason) return;
    try {
      await updateDoc(doc(db, 'newProducts', product.id), {
        status: 'rejected',
        rejectedBy: userInfo.uid,
        rejectedAt: Timestamp.now(),
        rejectionReason: reason
      });
      alert('신제품 신청이 거부되었습니다.');
      setShowModal(false);
      loadApplications();
    } catch (error) {
      console.error('거부 처리 실패:', error);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('ko-KR');
  };

  const filteredApplications = applications.filter(app => {
    if (!searchQuery) return true;
    return app.productName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           app.manufacturer?.toLowerCase().includes(searchQuery.toLowerCase()) ||
           app.businessName?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  const stats = {
    pending: applications.filter(a => a.status === 'pending').length,
    approved: applications.filter(a => a.status === 'approved').length,
    rejected: applications.filter(a => a.status === 'rejected').length,
    paid: applications.filter(a => a.isPaid).length
  };

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p style={styles.loadingText}>로딩 중...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div style={styles.headerIconBox}>
            <Package size={20} color="#ffffff" />
          </div>
          <span style={styles.headerTitle}>신제품 승인</span>
        </div>
      </div>

      {/* 통계 */}
      <div style={styles.statsGrid}>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #f59e0b' }}>
          <Clock size={24} color="#f59e0b" />
          <div style={styles.statInfo}>
            <span style={styles.statValue}>{stats.pending}</span>
            <span style={styles.statLabel}>대기중</span>
          </div>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #10b981' }}>
          <CheckCircle size={24} color="#10b981" />
          <div style={styles.statInfo}>
            <span style={styles.statValue}>{stats.approved}</span>
            <span style={styles.statLabel}>승인</span>
          </div>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #ef4444' }}>
          <XCircle size={24} color="#ef4444" />
          <div style={styles.statInfo}>
            <span style={styles.statValue}>{stats.rejected}</span>
            <span style={styles.statLabel}>거부</span>
          </div>
        </div>
        <div style={{ ...styles.statCard, borderLeft: '4px solid #6366f1' }}>
          <DollarSign size={24} color="#6366f1" />
          <div style={styles.statInfo}>
            <span style={styles.statValue}>{stats.paid}</span>
            <span style={styles.statLabel}>결제완료</span>
          </div>
        </div>
      </div>

      {/* 검색 & 필터 */}
      <div style={styles.searchSection}>
        <div style={styles.searchBox}>
          <Search size={18} color="#9ca3af" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="제품명, 제조사, 신청자 검색..."
            style={styles.searchInput}
          />
        </div>
      </div>

      <div style={styles.filterChips}>
        {[
          { key: 'all', label: '전체' },
          { key: 'pending', label: '대기중' },
          { key: 'approved', label: '승인' },
          { key: 'rejected', label: '거부' }
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            style={filter === f.key ? { ...styles.filterChip, ...styles.filterChipActive } : styles.filterChip}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* 목록 */}
      <div style={styles.listContainer}>
        {filteredApplications.length === 0 ? (
          <div style={styles.emptyContainer}>
            <div style={styles.emptyIconBox}>
              <Package size={40} color="#6366f1" />
            </div>
            <p style={styles.emptyText}>신제품 신청 내역이 없습니다</p>
          </div>
        ) : (
          <div style={styles.productList}>
            {filteredApplications.map(product => (
              <div
                key={product.id}
                style={styles.productCard}
                onClick={() => { setSelectedProduct(product); setShowModal(true); }}
              >
                {/* 카드 헤더 */}
                <div style={styles.cardHeader}>
                  {product.status === 'pending' ? (
                    <div style={styles.statusPending}><Clock size={14} /><span>대기중</span></div>
                  ) : product.status === 'approved' ? (
                    <div style={styles.statusApproved}><CheckCircle size={14} /><span>승인</span></div>
                  ) : (
                    <div style={styles.statusRejected}><XCircle size={14} /><span>거부</span></div>
                  )}
                  <div style={product.isPaid ? styles.paymentPaid : styles.paymentPending}>
                    <DollarSign size={14} />
                    <span>{product.isPaid ? '결제완료' : '결제대기'}</span>
                  </div>
                </div>

                {/* 이미지 & 정보 */}
                <div style={styles.cardBody}>
                  {product.imageUrl ? (
                    <img src={product.imageUrl} alt={product.productName} style={styles.productImage} />
                  ) : (
                    <div style={styles.noImage}><ImageIcon size={24} color="#cbd5e1" /></div>
                  )}
                  <div style={styles.productInfo}>
                    <h3 style={styles.productName}>{product.productName}</h3>
                    <div style={styles.infoRow}>
                      <Building2 size={14} color="#10b981" />
                      <span style={styles.manufacturer}>{product.manufacturer}</span>
                    </div>
                    {product.price && (
                      <span style={styles.price}>{product.price}</span>
                    )}
                  </div>
                </div>

                {/* 푸터 */}
                <div style={styles.cardFooter}>
                  <span style={styles.footerText}>{product.businessName || product.userName}</span>
                  <span style={styles.footerText}>{formatDate(product.createdAt)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 상세 모달 */}
      {showModal && selectedProduct && (
        <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <span style={styles.modalTitle}>신제품 상세정보</span>
              <button onClick={() => setShowModal(false)} style={styles.modalCloseBtn}>
                <X size={20} />
              </button>
            </div>

            <div style={styles.modalBody}>
              {/* 상태 뱃지 */}
              <div style={styles.modalBadges}>
                {selectedProduct.status === 'pending' ? (
                  <div style={styles.statusPending}><Clock size={14} /><span>대기중</span></div>
                ) : selectedProduct.status === 'approved' ? (
                  <div style={styles.statusApproved}><CheckCircle size={14} /><span>승인</span></div>
                ) : (
                  <div style={styles.statusRejected}><XCircle size={14} /><span>거부</span></div>
                )}
                <div style={selectedProduct.isPaid ? styles.paymentPaid : styles.paymentPending}>
                  <DollarSign size={14} />
                  <span>{selectedProduct.isPaid ? '결제완료' : '결제대기'}</span>
                </div>
              </div>

              {/* 이미지 */}
              {selectedProduct.imageUrl && (
                <img src={selectedProduct.imageUrl} alt={selectedProduct.productName} style={styles.modalImage} />
              )}

              {/* 정보 */}
              <h2 style={styles.modalProductName}>{selectedProduct.productName}</h2>
              <div style={styles.modalInfoRow}>
                <Building2 size={18} color="#10b981" />
                <span style={styles.modalManufacturer}>{selectedProduct.manufacturer}</span>
              </div>
              {selectedProduct.price && (
                <div style={styles.modalInfoRow}>
                  <DollarSign size={18} color="#6366f1" />
                  <span style={styles.modalPrice}>{selectedProduct.price}</span>
                </div>
              )}

              {/* 설명 */}
              <div style={styles.modalSection}>
                <h4 style={styles.modalSectionTitle}>제품 설명</h4>
                <p style={styles.modalDescription}>{selectedProduct.description}</p>
              </div>

              {/* 특징 */}
              {selectedProduct.features && (
                <div style={styles.modalSection}>
                  <div style={styles.modalFeatureHeader}>
                    <Star size={16} color="#f59e0b" />
                    <h4 style={styles.modalSectionTitle}>주요 특징</h4>
                  </div>
                  <p style={styles.modalDescription}>{selectedProduct.features}</p>
                </div>
              )}

              {/* 문의처 */}
              {selectedProduct.contactInfo && (
                <div style={styles.contactBox}>
                  <Phone size={16} color="#10b981" />
                  <div>
                    <span style={styles.contactLabel}>문의처</span>
                    <span style={styles.contactValue}>{selectedProduct.contactInfo}</span>
                  </div>
                </div>
              )}

              {/* 거부 사유 */}
              {selectedProduct.status === 'rejected' && selectedProduct.rejectionReason && (
                <div style={styles.rejectionBox}>
                  <h4 style={styles.rejectionTitle}>거부 사유</h4>
                  <p style={styles.rejectionReason}>{selectedProduct.rejectionReason}</p>
                </div>
              )}
            </div>

            {/* 액션 버튼 */}
            {selectedProduct.status === 'pending' && (
              <div style={styles.modalFooter}>
                <button onClick={() => handleReject(selectedProduct)} style={styles.rejectBtn}>
                  <XCircle size={18} />
                  <span>거부</span>
                </button>
                <button onClick={() => handleApprove(selectedProduct)} style={styles.approveBtn}>
                  <CheckCircle size={18} />
                  <span>승인</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f1f5f9' },

  // 헤더
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', backgroundColor: '#6366f1' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  headerIconBox: { width: '36px', height: '36px', borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  headerTitle: { fontSize: '20px', fontWeight: '700', color: '#ffffff' },

  // 통계
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', padding: '14px' },
  statCard: { display: 'flex', alignItems: 'center', gap: '12px', padding: '14px', backgroundColor: '#ffffff', borderRadius: '14px' },
  statInfo: { display: 'flex', flexDirection: 'column' },
  statValue: { fontSize: '20px', fontWeight: '700', color: '#0f172a' },
  statLabel: { fontSize: '12px', color: '#64748b', marginTop: '2px' },

  // 검색
  searchSection: { padding: '0 14px' },
  searchBox: { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', backgroundColor: '#ffffff', borderRadius: '10px' },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: '14px', color: '#0f172a' },

  // 필터
  filterChips: { display: 'flex', gap: '8px', padding: '14px', overflowX: 'auto' },
  filterChip: { padding: '8px 16px', backgroundColor: '#ffffff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', color: '#64748b', cursor: 'pointer', whiteSpace: 'nowrap' },
  filterChipActive: { backgroundColor: '#6366f1', color: '#ffffff' },

  // 목록
  listContainer: { padding: '0 14px 14px' },
  productList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  productCard: { backgroundColor: '#ffffff', borderRadius: '14px', padding: '14px', cursor: 'pointer' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  cardBody: { display: 'flex', gap: '12px', marginBottom: '12px' },
  productImage: { width: '70px', height: '70px', borderRadius: '10px', objectFit: 'cover' },
  noImage: { width: '70px', height: '70px', borderRadius: '10px', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  productInfo: { flex: 1 },
  productName: { margin: '0 0 6px', fontSize: '16px', fontWeight: '700', color: '#0f172a' },
  infoRow: { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' },
  manufacturer: { fontSize: '13px', color: '#10b981', fontWeight: '600' },
  price: { fontSize: '14px', fontWeight: '700', color: '#6366f1' },
  cardFooter: { display: 'flex', justifyContent: 'space-between', paddingTop: '12px', borderTop: '1px solid #f1f5f9' },
  footerText: { fontSize: '12px', color: '#94a3b8' },

  // 상태 뱃지
  statusPending: { display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', backgroundColor: '#fef3c7', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#92400e' },
  statusApproved: { display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', backgroundColor: '#d1fae5', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#065f46' },
  statusRejected: { display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', backgroundColor: '#fee2e2', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#991b1b' },
  paymentPaid: { display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', backgroundColor: '#dbeafe', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#1e40af' },
  paymentPending: { display: 'flex', alignItems: 'center', gap: '4px', padding: '5px 10px', backgroundColor: '#fee2e2', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#991b1b' },

  // 빈 상태
  emptyContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 20px' },
  emptyIconBox: { width: '80px', height: '80px', borderRadius: '40px', backgroundColor: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' },
  emptyText: { fontSize: '16px', fontWeight: '600', color: '#374151', margin: 0 },

  // 로딩
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '400px', gap: '16px' },
  spinner: { width: '40px', height: '40px', border: '4px solid #e2e8f0', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  loadingText: { fontSize: '14px', color: '#64748b' },

  // 모달
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 },
  modalContent: { width: '100%', maxWidth: '600px', maxHeight: '90vh', backgroundColor: '#ffffff', borderTopLeftRadius: '20px', borderTopRightRadius: '20px', overflow: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px', borderBottom: '1px solid #f1f5f9', position: 'sticky', top: 0, backgroundColor: '#ffffff', zIndex: 1 },
  modalTitle: { fontSize: '17px', fontWeight: '700', color: '#0f172a' },
  modalCloseBtn: { width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer' },
  modalBody: { padding: '18px' },
  modalBadges: { display: 'flex', gap: '10px', marginBottom: '16px' },
  modalImage: { width: '100%', borderRadius: '12px', marginBottom: '16px' },
  modalProductName: { margin: '0 0 12px', fontSize: '22px', fontWeight: '700', color: '#0f172a' },
  modalInfoRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' },
  modalManufacturer: { fontSize: '15px', fontWeight: '600', color: '#10b981' },
  modalPrice: { fontSize: '18px', fontWeight: '700', color: '#6366f1' },
  modalSection: { marginTop: '20px' },
  modalSectionTitle: { margin: '0 0 10px', fontSize: '14px', fontWeight: '700', color: '#0f172a' },
  modalDescription: { margin: 0, fontSize: '14px', color: '#475569', lineHeight: '1.6' },
  modalFeatureHeader: { display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' },
  contactBox: { display: 'flex', alignItems: 'center', gap: '12px', padding: '14px', backgroundColor: '#f0fdf4', borderRadius: '10px', marginTop: '16px' },
  contactLabel: { display: 'block', fontSize: '11px', color: '#166534', marginBottom: '2px' },
  contactValue: { fontSize: '14px', fontWeight: '600', color: '#166534' },
  rejectionBox: { padding: '14px', backgroundColor: '#fee2e2', borderRadius: '10px', marginTop: '16px' },
  rejectionTitle: { margin: '0 0 8px', fontSize: '13px', fontWeight: '700', color: '#991b1b' },
  rejectionReason: { margin: 0, fontSize: '13px', color: '#991b1b', lineHeight: '1.5' },
  modalFooter: { display: 'flex', gap: '10px', padding: '18px', borderTop: '1px solid #f1f5f9' },
  rejectBtn: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '14px', backgroundColor: '#fee2e2', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', color: '#dc2626', cursor: 'pointer' },
  approveBtn: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '14px', backgroundColor: '#10b981', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', color: '#ffffff', cursor: 'pointer' },
};

export default AdminProductApproval;